import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";
import { getValidToken } from "../auth/tokenManager";
import { natsClient, initializeNATS } from "../services/NATSClient";
import { orgNatsClient } from "../services/OrgNATSClient";
import { NavigationHistoryService } from "../services/NavigationHistoryService";
import { connectionHealthService } from "../services/ConnectionHealthService";
import { BackendDataService } from "../services/BackendDataService";
import {
  getActiveNatsUser,
  shouldAutoDemoFromToken,
  autoDemoReasonFromToken,
} from "../services/demoMode";
import { useSettingsStore } from "../store/SettingsStore";

interface SAPDataProviderProps {
  children: React.ReactNode;
}

/**
 * How long to wait for the NATS websocket before telling the user the data
 * service is unreachable. Generous enough to cover a slow connect plus the 10s
 * status poll below, short enough that nobody stares at a spinner wondering.
 */
const NATS_WAIT_GRACE_MS = 25000;

/**
 * Provider component that initializes SAP data when authentication is available
 */
export const SAPDataProvider: React.FC<SAPDataProviderProps> = ({
  children,
}) => {
  const auth = useUnifiedAuth();
  const { token, authenticated, initialized } = auth;
  const fetchSAPData = useSAPDataStore((state) => state.fetchSAPData);
  const setPendingData = useSAPDataStore((state) => state.setPendingData);
  const applyPendingData = useSAPDataStore((state) => state.applyPendingData);
  const hasPendingData = useSAPDataStore((state) => state.hasPendingData);
  const existingMachines = useSAPDataStore((state) => state.machines);
  const reset = useSAPDataStore((state) => state.reset);
  const [hasTried, setHasTried] = useState(false);
  const [keycloakReady, setKeycloakReady] = useState(false);
  const [natsReady, setNatsReady] = useState(false);
  const [natsInitialized, setNatsInitialized] = useState(false);
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const existingMachinesRef = useRef(existingMachines);

  // Keep refs updated
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    existingMachinesRef.current = existingMachines;
  }, [existingMachines]);

  // Listen for Keycloak ready event
  useEffect(() => {
    const handleKeycloakReady = () => {
      setKeycloakReady(true);
    };

    window.addEventListener("keycloak-ready", handleKeycloakReady);
    return () =>
      window.removeEventListener("keycloak-ready", handleKeycloakReady);
  }, []);

  // Set keycloakReady when already authenticated (event might have fired before listener)
  useEffect(() => {
    if (authenticated && initialized && !keycloakReady) {
      setKeycloakReady(true);
    }
  }, [authenticated, initialized, keycloakReady]);

  // Initialize NATS connection with authentication token
  useEffect(() => {
    const initializeNATSWithAuth = async () => {
      if (authenticated && token && !natsInitialized) {
        try {
          const natsServer =
            import.meta.env.VITE_NATS_URL || "wss://nats.enlixy.com:443";
          // Triggers 1+2 (no organisation / no workspace) are knowable from the
          // token alone, so decide BEFORE the first connect: a no-workspace user
          // must never attempt a real connection first. zustand setState is
          // synchronous, so the `getActiveNatsUser()` call just below already
          // observes `demo`. The one-way latch inside `enableAutoDemo` makes
          // this safe to call unconditionally on every init.
          if (shouldAutoDemoFromToken(token)) {
            const reason = autoDemoReasonFromToken(token);
            if (reason) {
              useSettingsStore.getState().enableAutoDemo(reason);
            }
          }
          // In demo mode this connects as NATS user "demo" with the same JWT.
          await initializeNATS([natsServer], token, getActiveNatsUser());

          // Initialize NavigationHistoryService after NATS is connected
          await NavigationHistoryService.initialize();

          // The organization's KPI targets (the charts' target lines); not
          // awaited, the charts draw them once loaded.
          void useSAPDataStore.getState().fetchKpiTargets(token);

          // Start connection health monitoring
          connectionHealthService.startHealthChecks(15000); // Check every 15 seconds (faster detection)

          // Send prewarm request to backend only on fresh login (not page refresh)
          if (!sessionStorage.getItem("nats-prewarmed")) {
            orgNatsClient.sendPrewarmRequest(token);
            sessionStorage.setItem("nats-prewarmed", "1");
          }

          setNatsInitialized(true);
        } catch (error) {
          console.error("SAPDataProvider: NATS initialization failed:", error);
          // Retry after delay
          setTimeout(() => {
            setNatsInitialized(false);
          }, 5000);
        }
      } else if (authenticated && token && natsInitialized) {
        // Token refreshed while NATS is already connected — just update stored token
        natsClient.updateToken(token);
      } else if (!authenticated) {
        // Reset NATS initialization status when user logs out
        connectionHealthService.stopHealthChecks();
        setNatsInitialized(false);
      }
    };

    initializeNATSWithAuth();
  }, [authenticated, token, natsInitialized]);

  // Monitor NATS connection status
  // Monitor NATS connection status (with less frequent checks)
  useEffect(() => {
    const checkNATSStatus = () => {
      const isConnected = natsClient.isConnected();
      setNatsReady(isConnected);
    };

    // Check initial status
    checkNATSStatus();

    // Set up interval to check NATS status (every 10 seconds, less aggressive)
    const interval = setInterval(checkNATSStatus, 10000);

    return () => clearInterval(interval);
  }, [natsInitialized]); // Depend on natsInitialized to check status after initialization

  /*
    Bound how long the overview may sit on its spinner waiting for NATS.

    `dataStatus` only moves off `idle` when a fetch actually runs, and a fetch
    needs `natsReady`. So a websocket that never connects means no fetch, no
    settled status, and a spinner with no end — the same symptom the empty-park
    fix removed, arriving by a different route. After the grace period we report
    it as unavailable so the user gets a real screen and a retry button.

    The store action is a no-op once a status exists, and a later successful
    connect runs the normal fetch (nothing has latched `hasTried`), so this
    cannot strand the view in a stale failure.
  */
  useEffect(() => {
    if (!authenticated || !token || natsReady) return;

    const timer = setTimeout(() => {
      useSAPDataStore
        .getState()
        .markDataUnavailable(
          "Could not reach the data service: the realtime connection was not established.",
        );
    }, NATS_WAIT_GRACE_MS);

    return () => clearTimeout(timer);
  }, [authenticated, token, natsReady]);

  // Enhanced data fetching with store-aware logic
  useEffect(() => {
    // Only fetch SAP data if:
    // 1. All auth conditions are met AND
    // 2. NATS is properly initialized AND connected AND
    // 3. Haven't tried yet
    const shouldFetchData =
      initialized &&
      keycloakReady &&
      authenticated &&
      token &&
      natsInitialized &&
      natsReady &&
      !hasTried;

    console.log("🔍 SAPDataProvider check:", {
      initialized,
      keycloakReady,
      authenticated,
      hasToken: !!token,
      natsInitialized,
      natsReady,
      hasTried,
      shouldFetchData,
    });

    if (shouldFetchData) {
      console.log("🔍 SAPDataProvider: Fetching SAP data...");
      // Latch before awaiting so a fast-settling promise cannot race a second
      // run of this effect into a duplicate fetch.
      setHasTried(true);
      fetchSAPData(token, true)
        .then(async () => {
          // The store action never rejects — it records the outcome in
          // `dataStatus`. Only transient failures earn a retry: a legitimately
          // empty park is a final answer, and retrying it would turn a correct
          // response into a 10s poll. Read via getState() so `dataStatus` does
          // not have to join this effect's dependency list.
          const status = useSAPDataStore.getState().dataStatus;

          // Trigger 3 (no machines): a CLEAN empty answer from a real
          // connection. Not `unavailable`/`error` — those are backend problems,
          // not "this account has no machines", and must keep their retry.
          if (status === "empty") {
            const settings = useSettingsStore.getState();
            // Only switch when we are NOT already in demo. The latch inside
            // `enableAutoDemo` is the real guard (at most once per session, and
            // a no-op once demo is active or the user opted out); reading back
            // `autoDemoMode` tells us whether it actually flipped so we only pay
            // for a reconnect when there is something to switch to.
            settings.enableAutoDemo("no-machines");
            const switched =
              useSettingsStore.getState().autoDemoMode && !settings.demoMode;
            if (switched) {
              // Targeted switch, not a reload: an empty park means zero
              // machines/lines/factories in the store, so no machine/line view
              // is mounted and no per-machine worker or live consumer exists.
              // The only owner of the real identity is the single NATS
              // connection, which `disconnect()` tears down completely (it
              // clears lastServers/lastToken/lastNatsUser so nothing
              // auto-reconnects). Reconnect as demo and re-run the fetch.
              const natsServer =
                import.meta.env.VITE_NATS_URL || "wss://nats.enlixy.com:443";
              await natsClient.disconnect();
              await initializeNATS([natsServer], token, getActiveNatsUser());
              setHasTried(false);
              void useSAPDataStore.getState().forceRefresh(token);
            }
            return;
          }

          if (status === "unavailable" || status === "error") {
            setTimeout(() => setHasTried(false), 10000);
          }
        })
        .catch((error) => {
          // Defensive: the store swallows its own fetch errors, so this is only
          // reachable if the action itself breaks.
          console.error("SAPDataProvider: SAP data fetch failed:", error);
          setTimeout(() => setHasTried(false), 10000);
        });
    } else if (!authenticated) {
      reset();
      setHasTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    keycloakReady,
    authenticated,
    token,
    natsInitialized,
    natsReady,
    hasTried,
  ]);

  // Listen for manual refresh requests and connection recovery
  useEffect(() => {
    const handleManualRefresh = () => {
      if (authenticated && token && natsInitialized && natsReady) {
        setHasTried(false); // Allow refetch
      }
    };

    const handleConnectionRecovered = () => {
      console.log(
        "🔄 SAPDataProvider: Connection recovered, refreshing data...",
      );
      if (authenticated && token && natsInitialized) {
        setNatsReady(true);
        setHasTried(false); // Allow refetch
      }
    };

    const handleNATSReconnected = () => {
      console.log("🔄 SAPDataProvider: NATS reconnected, refreshing data...");
      if (authenticated && token && natsInitialized) {
        setNatsReady(true);
        setHasTried(false); // Allow refetch
      }
    };

    // Listen for manual refresh events
    window.addEventListener("sap-data-manual-refresh", handleManualRefresh);
    window.addEventListener("connection-recovered", handleConnectionRecovered);
    window.addEventListener("nats-reconnected", handleNATSReconnected);

    return () => {
      window.removeEventListener(
        "sap-data-manual-refresh",
        handleManualRefresh,
      );
      window.removeEventListener(
        "connection-recovered",
        handleConnectionRecovered,
      );
      window.removeEventListener("nats-reconnected", handleNATSReconnected);
      // Stop health checks when component unmounts
      connectionHealthService.stopHealthChecks();
    };
  }, [authenticated, token, natsInitialized, natsReady]);

  // Background sync every 5 minutes to keep machine data fresh
  useEffect(() => {
    if (!authenticated || !token || !natsInitialized || !natsReady) {
      return;
    }

    const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

    const backgroundSync = async () => {
      const currentPath = locationRef.current;
      const isOnOverview = currentPath === "/" || currentPath === "/overview";

      try {
        // Get a fresh token (refreshes if needed)
        const freshToken = await getValidToken();

        // Fetch data silently
        const result = await BackendDataService.fetchSAPData(freshToken);

        // Only an `ok` payload is acted on. `empty` is deliberately ignored
        // here too: a silent background pass must never wipe a working machine
        // list. A genuine transition to an empty park is picked up by the next
        // foreground/forced fetch, which does render the empty state.
        if (result.status !== "ok") {
          return;
        }

        const newData = result.data;

        const newMachines =
          BackendDataService.transformSAPDataToMachines(newData);
        const existingMachines = existingMachinesRef.current;

        // Ignore if new data is empty but we have existing data (likely a failed request)
        if (newMachines.length === 0 && existingMachines.length > 0) {
          return;
        }

        // Compare: check if machine count or any machine availability changed
        const hasChanges =
          newMachines.length !== existingMachines.length ||
          newMachines.some((m) => {
            const existing = existingMachines.find(
              (e) => e.maschinennummer === m.maschinennummer,
            );
            return !existing || existing.datenVerfuegbar !== m.datenVerfuegbar;
          });

        if (hasChanges) {
          if (isOnOverview) {
            // On overview page, apply changes immediately
            console.log(
              "🔄 Background sync: Data changed, updating (overview page)...",
            );
            await fetchSAPData(freshToken, true);
          } else {
            // Not on overview, store as pending (no visual refresh)
            console.log(
              "🔄 Background sync: Data changed, storing as pending...",
            );
            setPendingData(newData);
          }
        }
        // If no changes: do nothing
      } catch (error) {
        // Silently ignore errors (token refresh, network issues, etc.)
      }
    };

    const intervalId = setInterval(backgroundSync, SYNC_INTERVAL);

    return () => clearInterval(intervalId);
  }, [authenticated, natsInitialized, natsReady, fetchSAPData, setPendingData]);

  // Apply pending data when navigating to overview
  useEffect(() => {
    const isOnOverview =
      location.pathname === "/" || location.pathname === "/overview";
    if (isOnOverview && hasPendingData) {
      applyPendingData();
    }
  }, [location.pathname, hasPendingData, applyPendingData]);

  return <>{children}</>;
};

export default SAPDataProvider;
